import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api, { apiError, fileUrl, API } from "@/lib/api";
import { StatusChip } from "@/components/StatusChip";
import { ShareResults } from "@/components/ShareResults";
import Footer from "@/components/Footer";
import { ShieldCheck, Trophy, DownloadSimple, Broadcast } from "@phosphor-icons/react";

export default function PublicResults() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get(`/public/results/${id}`).then(({ data }) => setData(data)).catch((e) => setError(apiError(e.response?.data?.detail)));
  }, [id]);

  if (error) return (
    <div className="min-h-[100dvh] flex items-center justify-center p-6 text-center">
      <div><h1 className="font-display font-bold text-2xl tracking-tight mb-2">Results unavailable</h1><p className="text-zinc-500">{error}</p></div>
    </div>
  );
  if (!data) return <div className="min-h-[100dvh] flex items-center justify-center text-zinc-400 font-mono text-sm">loading…</div>;

  const brand = data.org?.branding?.primary_color || "#1F6FE5";

  return (
    <div className="min-h-[100dvh] bg-[#F7F8F9] grain">
      <header className="backdrop-blur-xl bg-white/80 border-b border-zinc-200 sticky top-0 z-20">
        <div className="max-w-2xl mx-auto px-6 h-14 flex items-center gap-2">
          {data.org?.branding?.logo_url ? <img src={fileUrl(data.org.branding.logo_url)} alt="" className="h-7" /> : <div className="w-6 h-6 rounded-[4px]" style={{ background: brand }} />}
          <span className="font-semibold text-sm">{data.org.name}</span>
          <span className="ml-auto inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-zinc-400"><ShieldCheck size={13} weight="fill" style={{ color: brand }} /> Official results</span>
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-6 py-10 relative z-10">
        <div className="flex items-center gap-3 mb-2"><StatusChip status={data.election.status} /></div>
        <h1 className="font-display font-black text-4xl tracking-tighter mb-2">{data.election.title}</h1>
        <div className="inline-flex items-center gap-4 bg-white border border-zinc-200 px-5 py-3 mb-6">
          <div><div className="text-2xl font-display font-bold">{data.turnout.pct}%</div><div className="text-[10px] uppercase tracking-widest text-zinc-400">Turnout</div></div>
          <div className="h-8 w-px bg-zinc-200" /><span className="font-mono text-sm text-zinc-500">{data.turnout.voted} / {data.turnout.total}</span>
        </div>
        <div className="mb-8 flex flex-wrap items-center gap-2">
          <ShareResults
            imagePath={`/public/results/${id}/image`}
            shareUrl={`${window.location.origin}/results/${id}`}
            title={data.election.title}
            brand={brand}
          />
          <a data-testid="public-pdf-btn" href={`${API}/public/results/${id}/pdf`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 px-3.5 py-2.5 border border-zinc-300 rounded-md text-sm font-medium hover:border-black transition-colors">
            <DownloadSimple size={16} /> PDF report
          </a>
          {data.election.live_monitor !== false && (
            <a data-testid="public-live-btn" href={`/live/${id}`} className="inline-flex items-center gap-2 px-3.5 py-2.5 border border-zinc-300 rounded-md text-sm font-medium hover:border-black transition-colors">
              <Broadcast size={16} weight="fill" style={{ color: brand }} /> {data.election.status === "closed" ? "Live monitor" : "Watch live"}
            </a>
          )}
        </div>

        {data.winners && data.winners.length > 0 && (
          <div data-testid="winners-banner" className="mb-8 rounded-xl p-6 text-white" style={{ background: "#1B2A3A" }}>
            <div className="flex items-center gap-2 mb-4">
              <Trophy size={22} weight="fill" style={{ color: "#BF953F" }} />
              <h2 className="font-display font-bold text-xl tracking-tight">Official Winners</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {data.winners.map((w, i) => (
                <div key={w.position} data-testid={`winner-${i}`} className="flex items-center gap-3 bg-white/5 rounded-lg p-3">
                  <div className="w-11 h-11 rounded-full bg-white/10 overflow-hidden flex items-center justify-center shrink-0 text-sm font-bold" style={{ color: "#BF953F" }}>
                    {w.photo_url ? <img src={fileUrl(w.photo_url)} alt="" className="w-full h-full object-cover" /> : w.name[0]}
                  </div>
                  <div className="min-w-0">
                    <div className="text-[10px] uppercase tracking-widest text-zinc-400">{w.position}</div>
                    <div className="font-semibold truncate" style={{ color: "#BF953F" }}>{w.name}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="space-y-5">
          {data.results.map((r) => {
            const isRanked = r.ballot_type === "ranked";
            const max = Math.max(1, ...r.tally.map((c) => (isRanked ? c.points : c.votes)));
            return (
              <div key={r.position_id} className="bg-white border border-zinc-200 p-6">
                <h3 className="font-display font-bold text-lg tracking-tight mb-1">{r.title}</h3>
                <p className="text-xs font-mono text-zinc-400 mb-4">{r.total_ballots} ballots · {isRanked ? "Borda" : "Single choice"}</p>
                <div className="space-y-3">
                  {r.tally.map((c, i) => {
                    const v = isRanked ? c.points : c.votes;
                    return (
                      <div key={c.id}>
                        <div className="flex items-center justify-between text-sm mb-1">
                          <span className="flex items-center gap-1.5 font-medium">{i === 0 && r.total_ballots > 0 && <Trophy size={14} weight="fill" style={{ color: brand }} />}{c.name}</span>
                          <span className="font-mono text-zinc-500">{isRanked ? `${c.points} pts` : `${c.votes} · ${c.pct}%`}</span>
                        </div>
                        <div className="h-2 bg-zinc-100 rounded-full overflow-hidden">
                          <div className="h-full rounded-full transition-all" style={{ width: `${(v / max) * 100}%`, background: i === 0 ? brand : "#09090b" }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <Footer />
    </div>
  );
}
